import { useEffect, useState } from 'react';
import { X } from 'lucide-react';

/**
 * Singleton "undo send" toast. Composers call `showUndoSendToast` after
 * the API queues a pending send; the toast counts down from delaySeconds
 * and either fires `onCommit` (auto-send happens — composer can refresh
 * lists) or invokes `onCancel` if the agent clicks Undo.
 *
 * Implemented as a module-scoped event bus + a `<UndoSendToastHost>`
 * mounted once at the app root, so we don't need a Provider wrapper or
 * to bolt a full toast system into the codebase for one feature.
 */

interface ToastInput {
  outboxId: number;
  delaySeconds: number;
  /** What the toast shows on the left, e.g. "Sending in {N}s — to alice@…" */
  label?: string;
  /** Called when the user clicks Undo; should hit /mail/outbox/:id/cancel. */
  onCancel: () => void | Promise<void>;
  /** Called when the countdown hits zero with no cancel — composer can refresh queries. */
  onCommit?: () => void;
}

interface ToastState extends ToastInput {
  startedAt: number;
}

type Listener = (state: ToastState | null) => void;

const listeners = new Set<Listener>();
let current: ToastState | null = null;

function emit() {
  for (const l of listeners) l(current);
}

export function showUndoSendToast(input: ToastInput) {
  current = { ...input, startedAt: Date.now() };
  emit();
}

function dismiss() {
  current = null;
  emit();
}

export function UndoSendToastHost() {
  const [state, setState] = useState<ToastState | null>(current);
  const [now, setNow] = useState<number>(() => Date.now());
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const l: Listener = (s) => {
      setState(s);
      setBusy(false);
      setNow(Date.now());
    };
    listeners.add(l);
    return () => {
      listeners.delete(l);
    };
  }, []);

  useEffect(() => {
    if (!state) return;
    const tick = () => setNow(Date.now());
    const id = window.setInterval(tick, 250);
    return () => window.clearInterval(id);
  }, [state]);

  useEffect(() => {
    if (!state) return;
    const remaining = remainingSeconds(state, now);
    if (remaining <= 0) {
      // Auto-commit window expired. Worker will fire the send; let
      // composer refresh its query data via the optional onCommit hook,
      // then dismiss the toast.
      try {
        state.onCommit?.();
      } finally {
        dismiss();
      }
    }
  }, [state, now]);

  if (!state) return null;
  const remaining = Math.max(0, remainingSeconds(state, now));

  async function handleUndo() {
    if (busy) return;
    setBusy(true);
    try {
      await state!.onCancel();
    } finally {
      dismiss();
    }
  }

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        position: 'fixed',
        bottom: 24,
        left: '50%',
        transform: 'translateX(-50%)',
        background: 'var(--bg-card-alt)',
        color: 'var(--text)',
        border: '1px solid var(--border)',
        padding: '10px 14px',
        borderRadius: 'var(--radius-full)',
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        boxShadow: 'var(--shadow-lg)',
        zIndex: 'var(--z-toast)',
        fontSize: 13,
        maxWidth: 'calc(100% - 32px)',
      }}
    >
      <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {state.label ?? 'Sending'} in {remaining}s
      </span>
      <button
        type="button"
        onClick={handleUndo}
        disabled={busy}
        style={{
          background: 'transparent',
          border: '1px solid var(--primary)',
          color: 'var(--primary)',
          padding: '3px 12px',
          borderRadius: 'var(--radius-full)',
          cursor: busy ? 'wait' : 'pointer',
          fontSize: 12,
          fontWeight: 600,
          opacity: busy ? 0.6 : 1,
        }}
      >
        {busy ? 'Cancelling…' : 'Undo'}
      </button>
      <button
        type="button"
        onClick={dismiss}
        title="Dismiss (won't cancel send)"
        style={{
          background: 'transparent',
          border: 'none',
          color: 'var(--text-tertiary)',
          cursor: 'pointer',
          padding: 0,
          display: 'inline-flex',
        }}
      >
        <X size={14} />
      </button>
    </div>
  );
}

function remainingSeconds(state: ToastState, now: number): number {
  const elapsedMs = now - state.startedAt;
  return Math.ceil((state.delaySeconds * 1000 - elapsedMs) / 1000);
}
